/*
 * Footer CTA modal (SureCart/Onst registration form) tweaks.
 *
 * The Onst registration form is rendered by the app inside the modal and can
 * finish rendering after the modal is opened. On the first open of each
 * `modal-dialog.footer-cta-modal` we apply two DOM tweaks to the app markup:
 *   1. Move the `.onst-field` that wraps `.descriptionbox` to right after the
 *      `.final-step` div.
 *   2. Append a chevron icon after the text of the `.onst-final` submit button
 *      (`.onst-custom-form-submit`). The icon markup is rendered server-side
 *      into the inert `<template class="footer-cta-modal__chevron">` and cloned
 *      in here.
 * Both tweaks are idempotent and each is flagged once applied.
 */
(() => {
  const moveDescriptionField = (modal) => {
    const descriptionBox = modal.querySelector('.onst-field .descriptionbox');
    const finalStep = modal.querySelector('.final-step');
    if (!descriptionBox || !finalStep) return false;

    const field = descriptionBox.closest('.onst-field');
    if (!field) return false;

    // Already in place — nothing to do, but treat as handled.
    if (field !== finalStep.nextElementSibling) {
      finalStep.insertAdjacentElement('afterend', field);
    }
    return true;
  };

  const appendSubmitChevron = (modal) => {
    const button = modal.querySelector('.final-step .onst-custom-form-submit');
    if (!button) return false;
    if (button.dataset.chevronAdded) return true;

    const template = modal.querySelector('template.footer-cta-modal__chevron');
    if (!template) return true; // no icon to add — don't block the observer

    button.appendChild(template.content.cloneNode(true));
    button.dataset.chevronAdded = 'true';
    return true;
  };

  const applyTweaks = (modal) => {
    // Returns true only once every tweak has been applied.
    const movedDone = modal.dataset.descriptionFieldMoved === 'true' || moveDescriptionField(modal);
    if (movedDone) modal.dataset.descriptionFieldMoved = 'true';

    const chevronDone = modal.dataset.submitChevronAdded === 'true' || appendSubmitChevron(modal);
    if (chevronDone) modal.dataset.submitChevronAdded = 'true';

    return movedDone && chevronDone;
  };

  const handleFirstOpen = (modal) => {
    if (modal.dataset.tweaksApplied) return;

    // The form may already be present — try immediately.
    if (applyTweaks(modal)) {
      modal.dataset.tweaksApplied = 'true';
      return;
    }

    // Otherwise wait for the app to finish rendering the form.
    const contentObserver = new MutationObserver(() => {
      if (applyTweaks(modal)) {
        modal.dataset.tweaksApplied = 'true';
        contentObserver.disconnect();
      }
    });
    contentObserver.observe(modal, { childList: true, subtree: true });

    // Stop watching after a while so a form that never renders the expected
    // markup doesn't leave an observer running forever.
    setTimeout(() => contentObserver.disconnect(), 10000);
  };

  const observeModal = (modal) => {
    if (modal.hasAttribute('open')) handleFirstOpen(modal);

    const openObserver = new MutationObserver(() => {
      if (modal.hasAttribute('open')) handleFirstOpen(modal);
    });
    openObserver.observe(modal, { attributes: true, attributeFilter: ['open'] });
  };

  document.querySelectorAll('modal-dialog.footer-cta-modal').forEach(observeModal);
})();
